/**
 * Validated runtime env access for apps/admin.
 *
 * Production builds fail closed on unknown VITE_* keys via the
 * vite.config.ts allow-list. This module enforces the read side: a
 * single typed access point so feature code never reaches into
 * `import.meta.env` directly.
 */

/**
 * Which portal this admin build serves. Static const today —
 * `apps/admin` is the PMS portal. Multi-portal builds in the future
 * will route this through a build-time switch (e.g. `VITE_PORTAL`).
 * Surfaced here so feature code consumes `env.portal` rather than
 * inlining the string literal (per @serg-erin review M7).
 */
export type AdminPortal = 'PMS' | 'MSA' | 'GUEST';

export const env = Object.freeze({
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL ?? '',
  sentryDsn: import.meta.env.VITE_SENTRY_DSN ?? '',
  sentryEnvironment:
    import.meta.env.VITE_SENTRY_ENVIRONMENT ?? 'development',
  buildVersion: import.meta.env.VITE_BUILD_VERSION ?? 'dev',
  portal: 'PMS' as AdminPortal,
});

/**
 * Returns true if this is a Vite development build.
 *
 * Thin wrapper around `import.meta.env.DEV` (replaced at build time —
 * `true` under `pnpm --filter admin dev` and Vitest, `false` in
 * `vite build --mode production`). Exposed as a function rather than a
 * frozen-const field so tests can stub it via `vi.mock`, exercising the
 * production deny-by-default branch without `Object.defineProperty`
 * tricks on `import.meta.env`.
 *
 * Use sparingly. Build-mode branching is a code smell when it's
 * load-bearing for product behavior; reserve it for legacy-fallback or
 * demo-mode gates that explicitly should not survive a production build
 * — see SCRUM-15 reviewer fix #1 (the `me.portals === null` admin-
 * entitlement fallback in `auth/admin-portal-entitlement.ts`).
 */
export function isDevBuild(): boolean {
  return import.meta.env.DEV === true;
}
